import { useState, useCallback, useEffect } from "react";
import { toast } from "react-hot-toast";
import axios from "../../utils/axios";
import parseError from "../../utils/parseError";

/**
 * Fetches the order invoice as a PDF blob via the authenticated axios client
 * (the browser cannot pass the bearer token through `window.open`).
 *
 * Returns:
 *   { blobUrl, isFetching, fetchInvoice(lang), download(lang), revoke() }
 */
export const useGetOrderInvoice = (orderId) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [isFetching, setIsFetching] = useState(false);

    const revoke = useCallback(() => {
        setBlobUrl((current) => {
            if (current) URL.revokeObjectURL(current);
            return null;
        });
    }, []);

    // Always revoke when the orderId changes or the hook unmounts.
    useEffect(() => () => revoke(), [orderId, revoke]);

    const fetchInvoice = useCallback(
        async (lang) => {
            if (!orderId) return null;
            setIsFetching(true);
            try {
                const res = await axios.get(`/admin/orders/${orderId}/invoice`, {
                    params: lang ? { lang } : undefined,
                    responseType: "blob",
                });
                const url = URL.createObjectURL(
                    new Blob([res.data], { type: "application/pdf" })
                );
                // Replace the previous blob URL if any.
                setBlobUrl((prev) => {
                    if (prev) URL.revokeObjectURL(prev);
                    return url;
                });
                return url;
            } catch (error) {
                toast.error(parseError(error) || "Failed to load invoice");
                return null;
            } finally {
                setIsFetching(false);
            }
        },
        [orderId]
    );

    const download = useCallback(
        async (lang, filename) => {
            const url = await fetchInvoice(lang);
            if (!url) return;
            const a = document.createElement("a");
            a.href = url;
            a.download = filename || `invoice-${orderId}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        },
        [fetchInvoice, orderId]
    );

    return { blobUrl, isFetching, fetchInvoice, download, revoke };
};
